import React from "react";
import { Checkbox, Form, Input, Space, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import SearchSelect from "../../../components/SearchSelect";

const { Text } = Typography;

/**
 * "Use Client AI": the agent calls the provider with the client's own API key
 * instead of Kubit's. Tokens are still metered and priced, but the
 * subscription quotas never block the agent — only the client's provider
 * account limit applies. The key is write-only: the API never returns it.
 *
 * - `withCompany`: show the client picker (create form; the client cannot be
 *   changed afterwards).
 * - `isGlobal`: the agent has no client, so the option is disabled.
 * - `keyHint`: last characters of the stored key, when one is stored.
 */
const ClientAiFields = ({ withCompany = false, isGlobal = false, keyHint }) => {
  const form = Form.useFormInstance();
  const useClientAi = Form.useWatch("useClientAi", form);
  const hasStoredKey = !!keyHint;

  return (
    <>
      <Form.Item
        name="useClientAi"
        valuePropName="checked"
        style={{ marginBottom: useClientAi ? 12 : 0 }}
      >
        <Checkbox disabled={isGlobal && !withCompany}>
          <Space>
            Use Client AI
            <Tooltip title="The agent runs on the client's own provider account. Tokens are still calculated and billed, but subscription limits do not stop it — only the client's provider limit does.">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        </Checkbox>
      </Form.Item>

      {isGlobal && !withCompany && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Only an agent that belongs to a client can use the client's AI account.
        </Text>
      )}

      {useClientAi && (
        <>
          {withCompany && (
            <Form.Item
              name="companyId"
              label="Client"
              rules={[{ required: true, message: "Choose the client" }]}
            >
              <SearchSelect
                fetchKey="searchClients"
                endpoint="/clients/search"
                labelKey="name"
                valueKey="companyId"
                placeholder="Select client"
              />
            </Form.Item>
          )}
          <Form.Item
            name="clientAiApiKey"
            label="Client API key"
            extra={
              hasStoredKey
                ? `Stored key ${keyHint}. Leave blank to keep it.`
                : "The key of the client's account at the agent's provider. Stored encrypted and never shown again."
            }
            rules={
              hasStoredKey
                ? []
                : [{ required: true, message: "Enter the client's API key" }]
            }
          >
            <Input.Password
              autoComplete="new-password"
              placeholder={hasStoredKey ? "Leave blank to keep the stored key" : "sk-..."}
            />
          </Form.Item>
        </>
      )}
    </>
  );
};

export default ClientAiFields;
